import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid, Tooltip } from '@mui/material'
import { Edit, FlagCircle, Print } from '@mui/icons-material'

import {
  useDELETE_courseMutation,
  useGET_courseByIdQuery,
  usePOST_courseMutation,
  usePUT_courseMutation,
  useGET_courseComponentsQuery,
} from 'redux/api/coursesApi'
import { useGET_subjectsSelectQuery } from 'redux/api/subjectsApi'
import { Form, Formik } from 'formik'
import DialogPopup from 'components/molecules/DialogPopup'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import RichTextField from 'components/atoms/fields/RichTextField'
import SelectField from 'components/atoms/fields/SelectField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/courseModel'
import { crudColorMap, crudIconMap, gradeSelectData, printDiv, termSelectData, translateGradeLevel } from 'utils/helpers'

import { ActionType, ICourseData } from 'types'
import InputField from 'components/atoms/fields/InputField'
import GoalbankPage from 'pages/protected/GoalbankPage'
import { setCurriculumSelection, setGoalbankSelection } from 'redux/ui/goalbankUI'

import { AppDispatch, RootState } from 'redux/store'
import { useDispatch, useSelector } from 'react-redux'
import GoalbankSelection from 'components/utilized/goalbank/GoalbankSelection'
import CourseComponentList from 'components/molecules/CourseComponentList'
import InfoTooltip from 'components/atoms/buttons/InfoTooltip'
import SectionTitle from 'components/atoms/SectionTitle'
import CourseTemplate from 'components/utilized/courses/CourseTemplate'
import { useGET_setupOptionsQuery } from 'redux/api/setupApi'
import { useGET_mySchoolQuery } from 'redux/api/mySchoolApi'
import { useErrorAlert } from 'utils/customHooks/useErrorAlert'


interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const CourseForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
    
  const backToSlug = '/courses'
  const editSlug = `/courses/edit/${id}`

  const userData = useSelector((state: RootState) => state['user'].user)
  const { data: schoolData } = useGET_mySchoolQuery(null,{skip: !userData})

  const [totalError, setTotalError] = useState(null)
  useErrorAlert(totalError)

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.courses')}`,
    entityId: id,
    getEntityQuery: useGET_courseByIdQuery,
    createEntityMutation: usePOST_courseMutation,
    editEntityMutation: usePUT_courseMutation,
    deleteEntityMutation: useDELETE_courseMutation,
  })

  const { data: setupData } = useGET_setupOptionsQuery()

  const { data: defaultCourseComponents } = useGET_courseComponentsQuery({page: 1, default_only: 'true'})

  const {
    subject, 
    course_code, 
    grade_level,
    term,
    title, 
    description,
    course_objectives,
    learning_objectives,
    assessment,
    course_materials,
    technologies,
    instructions,
    outline,
    learner_support,
    plagiarism,
    components
  } = formModel()

  const serverValues = entity?.id
    ? {
      ...entity,
      grade_level: entity.grade_level >= 0 ? {
        id: entity.grade_level,
        title: translateGradeLevel(entity.grade_level, true)
      } : null,
      term: entity.term ? {
        id: entity.term,
        title: entity.term.toString()
      } : null,
      components: entity.components.map(c => {
        return {
          ...c,
          is_enabled: !c.is_disabled
        }
      })
    } : {}

  let formValues = {
    ...initialValues,
    ...serverValues,
  }


  if(defaultCourseComponents && formValues.components.length == 0)
    formValues.components = defaultCourseComponents.results.map(c => {
      return {
          parent_id: c.id,
          title: c.title,
          importance: c.importance,
          is_modifiable: c.is_modifiable,
          is_enabled: true,
      }
    }) 

  if(!formValues.plagiarism)
    formValues.plagiarism = `${t('common.plagiarism-default')}`

  const [goalbankOpen, setGoalbankOpen] = useState(false)
  const goalbankSelection = useSelector((state: RootState) => state['goalbank'].goalbankSelection)
  const curriculumSelection = useSelector((state: RootState) => state['goalbank'].curriculumSelection)

  useEffect(() => {
    dispatch(setGoalbankSelection(entity?.goalbank_selection ?? {}))
    dispatch(setCurriculumSelection(entity?.curriculum_selection ?? {}))
  }, [entity])

  return (
    setupData && <Formik
      enableReinitialize
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {

        const data: ICourseData = {
          ...values,
          subject_id: values.subject.id,
          grade_level: values.grade_level?.id ?? '0',
          term: values.term?.id ?? '0',
          course_code: values.course_code || "-",
          goalbank_selection: goalbankSelection,
          curriculum_selection: curriculumSelection,
          components: values.components.map(c => {
            return {
              ...c,
              is_disabled: !c.is_enabled
            }
          })
        }

     
        if(!id && schoolData?.default_grading_type === 'categories'){
          const total = values.components.filter(c => c.is_enabled).reduce((acc, curr) => acc + curr.importance, 0)
          if(total !== 100){
            setTotalError({
              data: [{
                field: 'non_field_errors',
                message: t('common.grading-categories-total')
              }]
            })
          }else{
            setTotalError(null)
            handleSubmit(action, id, data)
          }
        }else{
          handleSubmit(action, id, data)
        }

      }}
    >
    {({ values, setFieldValue, errors }) => {
     return (
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>


          <Grid item xs={12} lg={9}>
            <InputField fieldProps={{ ...title }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12} lg={3}>
            <InputField fieldProps={{ ...course_code }} disabled={viewOnly} />
          </Grid>

          <Grid item xs={12} lg={4}>
            <SelectField
              fieldProps={{ ...subject }}
              apiCall={useGET_subjectsSelectQuery}
              disabled={viewOnly}
            />
          </Grid>

          <Grid item xs={12} lg={2}>
            <SelectField
                fieldProps={{ ...grade_level }}
                apiCall={gradeSelectData}
                disabled={viewOnly}
              />
          </Grid>
          <Grid item xs={12} lg={2}>
              <SelectField
                fieldProps={{ ...term }}
                apiCall={termSelectData}
                disabled={viewOnly}
              />
          </Grid>
       

          {setupData.filter(sd => sd.slug == "course_curriculum" && sd.is_enabled).length == 1 && 
          <><Grid item xs={12} lg={4}>
              <Button
                onClick={() => setGoalbankOpen(true)}
                aria-label={`${t('goalbank.use-curriculum-library')}`}
                size='small'
                disabled={viewOnly}
                color='success'
                variant='contained'
                sx={{ boxShadow: 'none',  minHeight: '47px' }}
               >
                 <FlagCircle style={{ fontSize: '1.5rem', marginRight: '10px' }} /> {t('goalbank.use-curriculum-library')}
              </Button>
          </Grid>


          <Grid item xs={12}>
            <GoalbankSelection goalbankSelection={goalbankSelection} curriculumSelection={curriculumSelection}/>
          </Grid>
          </>}

          {setupData.filter(sd => sd.slug == "course_description" && sd.is_enabled).length == 1 && 
              <Grid item xs={12}>
                <RichTextField
                  fieldProps={{ ...description }}
                  disabled={viewOnly}
                  label={`${t('common.course-description')}`}
                />
              </Grid>}
          

           {/* 
          <Grid item xs={6}>
            <RichTextField
              fieldProps={{ ...course_objectives }}
              disabled={viewOnly}
              label={`${t('common.course-objectives')}`}
            />
          </Grid>
          <Grid item xs={6}>
            <RichTextField
                fieldProps={{ ...learning_objectives }}
                disabled={viewOnly}
                label={`${t('lessons.learning-objectives')}`}
              />
          </Grid>
          */}

          {setupData.filter(sd => sd.slug == "course_assessment" && sd.is_enabled).length == 1 && 
          <Grid item xs={6}>
            <RichTextField
              fieldProps={{ ...assessment }}
              disabled={viewOnly}
              label={`${t('common.assessment-and-measurments')}`}
            />
          </Grid>}

          {setupData.filter(sd => sd.slug == "course_materials" && sd.is_enabled).length == 1 &&   
          <Grid item xs={6}>
            <RichTextField
              fieldProps={{ ...course_materials }}
              disabled={viewOnly}
              label={`${t('common.course-materials')}`}
            />
          </Grid>}

          {setupData.filter(sd => sd.slug == "course_technologies" && sd.is_enabled).length == 1 &&   
          <Grid item xs={6}>
            <RichTextField
              fieldProps={{ ...technologies }}
              disabled={viewOnly}
              label={`${t('common.course-technologies')}`}
            />
          </Grid>}


          {setupData.filter(sd => sd.slug == "course_instructions" && sd.is_enabled).length == 1 &&   
          <Grid item xs={6}>
            <RichTextField
              fieldProps={{ ...instructions }}
              disabled={viewOnly}
              label={`${t('common.course-instructions-and-activities')}`}
            />
            </Grid>
          }

          {setupData.filter(sd => sd.slug == "course_outline" && sd.is_enabled).length == 1 &&   
          <Grid item xs={6}>
            <RichTextField
              fieldProps={{ ...outline }}
              disabled={viewOnly}
              label={`${t('common.course-outline')}`}
            />
          </Grid>
          }

          {setupData.filter(sd => sd.slug == "course_learner_support" && sd.is_enabled).length == 1 &&  
          <Grid item xs={6}>
            <RichTextField
              fieldProps={{ ...learner_support }}
              disabled={viewOnly}
              label={`${t('common.learner-support')}`}
            />
          </Grid>
          }

          {setupData.filter(sd => sd.slug == "course_plagiarism" && sd.is_enabled).length == 1 &&   
          <Grid item xs={12}>
            <RichTextField
              fieldProps={{ ...plagiarism }}
              disabled={viewOnly}
              label={`${t('common.plagiarism')}`}
            />
          </Grid>}

          {setupData.filter(sd => sd.slug == "course_grading_plan" && sd.is_enabled).length == 1 && <>
          <Grid item xs={12}>
              <SectionTitle text={`${t('common.grading-plan')}`} 
                icon={<InfoTooltip hint={t('common.grading-plan')}></InfoTooltip>}/>
         </Grid>  

          <Grid item xs={12} lg={12}>
            <CourseComponentList 
              fieldProps={{ ...components }} 
              showImportance={schoolData?.default_grading_type === 'categories'} 
              defaultOnly={false} 
              disabled={viewOnly || !!id} />
          </Grid> </>}


          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>


              {entity && entity.id && action !== "delete" && 
                  <Button
                        variant='outlined'
                        color='black'
                        onClick={ () => { printDiv('course-template') } }
                        sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                      >
                        <Print style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                        {`${t('common.print')}`}
                  </Button>
                  }

              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('actions.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>


        {entity && entity.id &&
                <Box id="course-template" sx={{ display: 'none' }}>
                    <CourseTemplate entity={entity} />
                </Box> }

        <DialogPopup
              title={t('common.goalbank')}
              open={goalbankOpen}
              onClose={() => setGoalbankOpen(false)}
              popupWidth={'1400px'}
              >
                  <GoalbankPage hasSelection={true} setupPrefix="course" 
                    visibleSections={['course_chapters', 'measures', 'highlights', 'success_criterias']}
                    filters={{
                        grade_level: values.grade_level?.id ?? null,
                        subject: values.subject?.id ?? null,
                  }}/>
        </DialogPopup>
      </Form>
        )
      }}
    </Formik>
  )
}
export default CourseForm
